import { useState, useEffect } from "react";
import { useAuthContext } from "hooks/useAuthContext";

import {
  MenuItem,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { useHandleProjectSubmission } from "hooks/useHandleProjectSubmission";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { API_URL } from "../../../../config";
import { toast } from "sonner";

const RegisterProject = ({ onRegister, onCancel }) => {
  const { user } = useAuthContext();

  const [projectTitle, setProjectTitle] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Check for small screen size

  const {
    handleprojectsubmission,
    isLoading,
    error,
    success,
    setError,
    setSuccess,
  } = useHandleProjectSubmission();

  useEffect(() => {
    const mdInput = document.querySelector(".MuiSelect-select");
    if (mdInput) {
      mdInput.style.height = "40px";
    }
  }, []);

  useEffect(() => {
    if (error) {
      toast.error("Error", { description: error });
    }
    if (success) {
      toast.success("Success", { description: success });
    }
  }, [error, setError, success, setSuccess]);

  // Fetch projects from the server
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await fetch(
          `${API_URL}/supervisors/getavailablesupervisors`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        const supervisors = await response.json();
        if (response.ok) {
          if (supervisors.length > 0) {
            setSupervisors(supervisors);
          } else {
            setSupervisors([]);
          }
        }
        if (!response.ok) {
          toast.error("Failed to fetch supervisors");
        }
      } catch (error) {
        toast.error("Failed to fetch supervisors");
        setSupervisors([]);
      }
    };

    if (user) {
      fetchFiles();
    }
  }, [user]);

  const handleProjectSubmission = async (e) => {
    e.preventDefault();
    // Check if a supervisor has been selected
    if (!selectedSupervisor) {
      toast.warning("Warning", { description: "Please select a supervisor" });
      return;
    }

    // Get the email of the selected supervisor
    const selectedSupervisorEmail =
      selectedSupervisor && selectedSupervisor !== "None"
        ? supervisors.find((supervisor) => supervisor.id === selectedSupervisor)
            .email
        : "None";
    // Handle submission logic here
    await handleprojectsubmission(
      projectTitle,
      projectDescription,
      selectedSupervisorEmail
    );
  };

  useEffect(() => {
    if (success) {
      onRegister();
    }
  }, [success, onRegister, setError, setSuccess]);

  return (
    <MDBox
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "350px",
      }}
    >
      <MDBox
        position="relative"
        color="white"
        bgColor="transparent"
        variant="gradient"
        borderRadius="lg"
        shadow="lg"
        opacity={1}
        p={3}
        mt={0}
        height={350}
        width={isSmallScreen ? "100%" : 800} // Adjust width for small screens
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        <IconButton
          onClick={onCancel}
          sx={
            isSmallScreen
              ? { position: "absolute", top: 20, left: 0, color: "blue" }
              : { position: "absolute", top: 20, left: 20, color: "blue" }
          } // Adjust position as needed
        >
          <ArrowBack />
        </IconButton>
        <MDTypography
          variant="h5"
          sx={{ color: "black", fontWeight: "bold", textAlign: "center" }}
        >
          Get Started With Your Research Journey
        </MDTypography>
        <MDInput
          label="Project Title"
          value={projectTitle}
          onChange={(e) => setProjectTitle(e.target.value)}
          placeholder="Project Title here"
          fullWidth
          mb={2}
          required
        />
        <MDInput
          label="Project Description"
          value={projectDescription}
          onChange={(e) => setProjectDescription(e.target.value)}
          placeholder="Insert a brief description of your project"
          fullWidth
          multiline
          rows={isSmallScreen ? 2 : 4} // Adjust number of rows for small screens
          mb={2}
          required
        />
        <MDInput
          label="Select Supervisor"
          select
          value={selectedSupervisor}
          onChange={(e) => setSelectedSupervisor(e.target.value)}
          fullWidth
          mb={3}
          required
          SelectProps={{
            MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
          }} // Set max height for the dropdown menu
        >
          <MenuItem value="None" disabled>
            <em>None</em>
          </MenuItem>
          {supervisors.map((supervisor) => (
            <MenuItem key={supervisor.id} value={supervisor.id}>
              {supervisor.first_name} {supervisor.last_name}
            </MenuItem>
          ))}
        </MDInput>
        <MDButton
          variant="contained"
          color="info"
          onClick={handleProjectSubmission}
          disabled={isLoading} // Disable button while loading
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" /> // Show spinner when loading
          ) : (
            "Submit" // Show the text when not loading
          )}
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default RegisterProject;
